import { useParams, useSearchParams } from "react-router-dom";
import { Dropdown, Flex } from "@fluentui/react-northstar";
import { DateRangePickerOneMonth } from "../../../../components/DateRangePicker/DateRangePickerOneMonth";
import {
    ButtonReset,
    DropdownFilter,
} from "../../../hr_documents/styles/index.styles";
import {
    useQGetExpenseCategories,
    useQGetExpenseUsersAndDepartments,
    useQGetManagers,
} from "../../api/queries";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { DEVICE } from "../../../../constants/responsive";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { I18N_NS_EXPENSE } from "../../constants";

const REBILLABLE_OPTIONS = [
    {
        id: 0,
        header: "Tout",
    },
    {
        id: 1,
        header: "Facturable",
    },
    {
        id: 2,
        header: "Non facturable",
    },
];

const FilterExpensesForm = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { role } = useParams();
    const { t } = useTranslation(I18N_NS_EXPENSE);
    // * STATES
    const [rebillable, setRebillable] = useState(null);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [managers, setManagers] = useState([]);
    const [resetDatesToNull, setResetDatesToNull] = useState(null);
    const [usersAndDepartments, setUsersAndDepartments] = useState([]);
    const [status, setStatus] = useState(1);

    useEffect(() => {
        resetFilters();
    }, [role]);

    // * QUERIES
    const {
        format_data: expense_categories_formatted,
        isLoading: isExpenseCategoriesLoading,
    } = useQGetExpenseCategories();

    const {
        format_data: expense_users_and_departments_formatted,
        isLoading: isExpenseUsersAndDepartmentsLoading,
    } = useQGetExpenseUsersAndDepartments();

    const {
        format_data: expense_managers_formatted,
        isLoading: isExpenseManagersLoading,
    } = useQGetManagers();

    // * FUNCTIONS
    const resetFilters = () => {
        setRebillable(null);
        setExpenseCategories([]);
        setManagers([]);
        setUsersAndDepartments([]);
        setResetDatesToNull(new Date().getTime());
        setStatus(1);
        return setSearchParams();
    };

    // * VALUES
    const statusItems = useMemo(
        () => [
            {
                id: 0,
                header: t("filter.status.all-notes"),
            },
            { id: 1, header: t("filter.status.to-pay") },
        ],
        [t]
    );
    const defaultSelectedRebillable =
        REBILLABLE_OPTIONS.find((i) => i.id == searchParams.get("r")) ?? null;
    const defaultSelectedStatus =
        statusItems.find((i) => i.id == searchParams.get("tp")) ??
        statusItems[1];
    const defaultSelectedObjectsBudget = searchParams.getAll("ec");
    const defaultSelectedObjectsManager = searchParams.getAll("m");
    const defaultSelectedObjectsUser = searchParams.getAll("ud");

    const disable_reset_button =
        (searchParams.get("r") === null || searchParams.get("r") === "") &&
        (searchParams.get("ec") === null || searchParams.get("ec") === "") &&
        (searchParams.get("m") === null || searchParams.get("m") === "") &&
        (searchParams.get("ud") === null || searchParams.get("ud") === "") &&
        (searchParams.get("sd") === null || searchParams.get("sd") === "") &&
        (searchParams.get("ed") === null || searchParams.get("ed") === "") &&
        (searchParams.get("tp") === null || searchParams.get("tp") === "");

    return (
        <Container wrap styles={{ gap: "10px" }}>
            <Flex styles={{ gap: "10px" }}>
                {/* dates (start & end) */}
                <Flex>
                    <DateRangePickerOneMonth
                        defaultStartDate={searchParams.get("sd")}
                        defaultEndDate={searchParams.get("ed")}
                        resetDatesToNull={resetDatesToNull}
                        onDatesChange={(dates) => {
                            if (dates.end !== null && dates.start !== null) {
                                const newParams = new URLSearchParams(
                                    searchParams
                                );
                                const sd =
                                    dates.start.toLocaleDateString("fr-FR");
                                newParams.set(
                                    "sd",
                                    moment(sd, "DD/MM/YYYY").format(
                                        "YYYY-MM-DD"
                                    )
                                );
                                const ed =
                                    dates.end.toLocaleDateString("fr-FR");
                                newParams.set(
                                    "ed",
                                    moment(ed, "DD/MM/YYYY").format(
                                        "YYYY-MM-DD"
                                    )
                                );
                                return setSearchParams(newParams);
                            } else {
                                const newParams = new URLSearchParams(
                                    searchParams
                                );
                                newParams.delete("sd");
                                newParams.delete("ed");
                                return setSearchParams(newParams);
                            }
                        }}
                    />
                </Flex>
                {/* type d'absence */}
                <Flex>
                    <DropdownFilter
                        className="category"
                        placeholder="Choisir une catégorie de dépense"
                        items={expense_categories_formatted || []}
                        onChange={(e, i) => {
                            const newParams = new URLSearchParams(searchParams);
                            newParams.delete("ec");
                            const value = i.value;
                            if (value.length === 0) {
                                setExpenseCategories([]);
                            } else {
                                value.forEach((val) =>
                                    newParams.append("ec", val.id)
                                );
                            }
                            setExpenseCategories(value);
                            return setSearchParams(newParams);
                        }}
                        value={
                            expenseCategories?.length > 0
                                ? expenseCategories
                                : expense_categories_formatted?.filter(
                                      (option) =>
                                          defaultSelectedObjectsBudget.includes(
                                              option.id + ""
                                          )
                                  ) ?? []
                        }
                        multiple
                        inverted
                        fluid
                        search
                        width={"206px"}
                    />
                </Flex>
                {/* facturable ou pas  */}
                <Flex>
                    <DropdownFilter
                        className="category"
                        placeholder="Facturable ou non"
                        items={REBILLABLE_OPTIONS}
                        onChange={(e, i) => {
                            const newParams = new URLSearchParams(searchParams);
                            newParams.delete("r");
                            const value = i.value;
                            newParams.append("r", value.id);
                            setRebillable(value);
                            return setSearchParams(newParams);
                        }}
                        inverted
                        checkable
                        value={defaultSelectedRebillable}
                        fluid
                        width={"206px"}
                    />
                </Flex>
            </Flex>
            <Flex styles={{ gap: "10px" }}>
                {/* départments list */}
                <Flex>
                    <DropdownFilter
                        className="category"
                        placeholder="Collab. & Dép."
                        items={expense_users_and_departments_formatted || []}
                        onChange={(e, i) => {
                            const newParams = new URLSearchParams(searchParams);
                            newParams.delete("ud");
                            const value = i.value;
                            if (value.length === 0) {
                                setUsersAndDepartments([]);
                            } else {
                                value.forEach((val) =>
                                    newParams.append("ud", val.id)
                                );
                            }
                            setUsersAndDepartments(value);
                            return setSearchParams(newParams);
                        }}
                        value={
                            usersAndDepartments?.length > 0
                                ? usersAndDepartments
                                : expense_users_and_departments_formatted?.filter(
                                      (option) =>
                                          defaultSelectedObjectsUser.includes(
                                              option.id + ""
                                          )
                                  ) ?? []
                        }
                        multiple
                        inverted
                        fluid
                        search
                        width={"206px"}
                    />
                </Flex>

                {/* manager */}
                <Flex>
                    <DropdownFilter
                        className="category"
                        placeholder="Managers"
                        items={expense_managers_formatted}
                        onChange={(e, i) => {
                            const newParams = new URLSearchParams(searchParams);
                            newParams.delete("m");
                            const value = i.value;
                            if (value.length === 0) {
                                setManagers([]);
                            } else {
                                value.forEach((val) =>
                                    newParams.append("m", val.id)
                                );
                            }
                            setManagers(value);
                            return setSearchParams(newParams);
                        }}
                        value={
                            managers?.length > 0
                                ? managers
                                : expense_managers_formatted?.filter((option) =>
                                      defaultSelectedObjectsManager.includes(
                                          option.id + ""
                                      )
                                  ) ?? []
                        }
                        multiple
                        inverted
                        fluid
                        search
                        width={"206px"}
                    />
                </Flex>
                <Flex column>
                    <DropdownFilter
                        items={statusItems}
                        defaultValue={statusItems.at(1)}
                        onChange={(e, i) => {
                            const newParams = new URLSearchParams(searchParams);
                            const PARAM_KEY = "tp";
                            newParams.delete(PARAM_KEY);
                            const value = i.value.id;
                            newParams.append(PARAM_KEY, value);
                            setStatus(value);
                            return setSearchParams(newParams);
                        }}
                        value={defaultSelectedStatus}
                        checkable
                        inverted
                        width={"206px"}
                        fluid
                    />
                </Flex>
                <Flex>
                    <ButtonReset
                        text
                        disabled={disable_reset_button}
                        className={disable_reset_button ? "disabled" : ""}
                        onClick={resetFilters}
                    >
                        Réinitialiser
                    </ButtonReset>
                </Flex>
            </Flex>
        </Container>
    );
};

export default FilterExpensesForm;

const Container = styled(Flex)`
    @media ${DEVICE.mobileS} {
        width: clamp(300px, 90vw, 750px);
    }
    @media ${DEVICE.mobileM} {
        width: clamp(300px, 95vw, 750px);
    }
    @media ${DEVICE.mobileL} {
    }
    @media ${DEVICE.tablet} {
    }
    @media ${DEVICE.laptop} {
        width: clamp(300px, 55vw, 750px);
    }
`;
